import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { makePdf } from "./Reports";
import { inr, downloadHundialPdf, WaStatus } from "./HundialEntry";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FileDown, FileSpreadsheet, RefreshCw, Send, Trash2, Landmark } from "lucide-react";
import { toast } from "sonner";

const dmy = (d) => (d ? d.split("-").reverse().join("/") : "-");
const grams = (m) => Number(m?.total_g || 0);
const fmtG = (v) => `${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} g`;

export default function HundialReports() {
  const { user } = useAuth();
  const isSA = user.role === "SUPER_ADMIN";
  const canSend = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const [temples, setTemples] = useState([]);
  const [f, setF] = useState({ frm: "", to: "", temple_id: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/meta/form-data").then((r) => setTemples(r.data.temples || [])).catch(() => {}); }, []);
  const load = () => {
    setBusy(true);
    api.get("/hundial/reports", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) })
      .then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))).finally(() => setBusy(false));
  };
  useEffect(load, []); // eslint-disable-line

  const tname = (tid) => temples.find((t) => t.temple_id === tid)?.name || tid;
  const totals = useMemo(() => rows.reduce((a, r) => ({
    cash: a.cash + Number(r.cash_total || 0), gold: a.gold + grams(r.gold), silver: a.silver + grams(r.silver),
    old: a.old + Number(r.old_notes_total || 0), days: a.days + Number(r.period_days || 0),
  }), { cash: 0, gold: 0, silver: 0, old: 0, days: 0 }), [rows]);
  // Consolidated (Super Admin): one line per temple
  const byTemple = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const t = m[r.temple_id] || (m[r.temple_id] = { temple_id: r.temple_id, reports: 0, cash: 0, gold: 0, silver: 0, old: 0 });
      t.reports += 1; t.cash += Number(r.cash_total || 0); t.gold += grams(r.gold); t.silver += grams(r.silver); t.old += Number(r.old_notes_total || 0);
    });
    return Object.values(m);
  }, [rows]);

  const resend = async (r) => {
    try { const res = await api.post(`/hundial/reports/${r.id}/send`, {}); toast.success(`Sent to ${res.data.whatsapp.length} WhatsApp numbers (all deliveries)`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete Hundial report ${r.report_no}? This cannot be undone.`)) return;
    try { await api.delete(`/hundial/reports/${r.id}`); toast.success("Report deleted (audited)"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const head = ["S.No", ...(isSA ? ["Temple"] : []), "Report No", "Opened", "Last Opened", "Days", "Gold", "Silver", "Notes", "Coins", "Total Rupees", "Avg / Day", "Old Notes"];
  const line = (r, i) => [i + 1, ...(isSA ? [tname(r.temple_id)] : []), r.report_no, dmy(r.opened_date), dmy(r.last_opened_date), r.period_days,
    fmtG(grams(r.gold)), fmtG(grams(r.silver)), inr(r.cash_notes), inr(r.cash_coins), inr(r.cash_total), inr(r.avg_per_day), inr(r.old_notes_total)];
  const period = f.frm || f.to ? `${dmy(f.frm) || "start"} to ${dmy(f.to) || "today"}` : "All dates";
  const temple = temples.find((t) => t.temple_id === (f.temple_id || user.temple_id));

  const exportPdf = () => {
    if (!rows.length) { toast.error("No reports to export"); return; }
    const rs = (v) => `Rs ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const footMap = { "S.No": "Total", Days: String(totals.days), Gold: fmtG(totals.gold), Silver: fmtG(totals.silver), "Total Rupees": rs(totals.cash), "Old Notes": rs(totals.old) };
    makePdf({
      temple: isSA && !f.temple_id ? null : temple, templeName: isSA && !f.temple_id ? "All Temples" : undefined,
      title: `Hundial Opening Reports - ${period}`, landscape: true, fontSize: 7,
      head: head.map(String), body: rows.map((r, i) => line(r, i).map((v) => String(v).replace("₹", "Rs "))),
      foot: head.map((h) => footMap[h] || ""),
      filename: `Hundial_Reports_${new Date().toISOString().slice(0, 10)}.pdf`,
    });
  };
  const exportExcel = async () => {
    if (!rows.length) { toast.error("No reports to export"); return; }
    const XLSX = await import("xlsx");
    const sheet = rows.map((r, i) => ({
      "S.No": i + 1, ...(isSA ? { Temple: tname(r.temple_id) } : {}), "Report No": r.report_no, "Bank": r.bank_name, "Branch": r.branch_name, "Manager": r.manager_name,
      "Last Opened": dmy(r.last_opened_date), "Opened": dmy(r.opened_date), "Days": r.period_days,
      "Gold (Kgs)": r.gold?.kg, "Gold (Gms)": r.gold?.g, "Gold (Mgrs)": r.gold?.mg, "Gold Total (g)": grams(r.gold),
      "Silver (Kgs)": r.silver?.kg, "Silver (Gms)": r.silver?.g, "Silver (Mgrs)": r.silver?.mg, "Silver Total (g)": grams(r.silver),
      "Currency Notes": r.cash_notes, "Currency Coins": r.cash_coins, "Total Rupees": r.cash_total, "Average / Day": r.avg_per_day,
      "Foreign Currency": (r.foreign_totals || []).map((t) => `${t.currency} ${t.amount}`).join(", "),
      "Old Notes Value": r.old_notes_total, "Entered By": r.created_by, "WhatsApp": (r.whatsapp || []).map((w) => `${w.mobile}: ${w.status}`).join(", "),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), "Hundial Reports");
    if (isSA) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byTemple.map((t) => ({
      Temple: tname(t.temple_id), Reports: t.reports, "Total Rupees": t.cash, "Gold (g)": t.gold, "Silver (g)": t.silver, "Old Notes": t.old }))), "Consolidated");
    XLSX.writeFile(wb, `Hundial_Reports_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div data-testid="hundial-reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 flex items-center gap-2"><Landmark className="w-7 h-7 text-gold-600" /> Hundial Reports</h1>
      <p className="text-sm text-slate-500 mb-4">{isSA && !f.temple_id ? "All temples" : temple?.name} · {period}</p>
      <Card className="mb-4"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs">From (opening date)</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></div>
        {isSA ? (
          <div><Label className="text-xs">Temple</Label>
            <select value={f.temple_id} onChange={(e) => setF({ ...f, temple_id: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="hundial-rep-temple">
              <option value="">All temples</option>{temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
            </select></div>
        ) : <div className="hidden md:block" />}
        <Button onClick={load} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="hundial-rep-run"><RefreshCw className="w-4 h-4 mr-1" /> View</Button>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={exportPdf} data-testid="hundial-rep-pdf"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" className="flex-1" onClick={exportExcel} data-testid="hundial-rep-excel"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[["Reports", rows.length], ["Total Rupees", inr(totals.cash)], ["Gold", fmtG(totals.gold)], ["Silver", fmtG(totals.silver)], ["Old Notes", inr(totals.old)]].map(([l, v]) => (
          <Card key={l} className="card-gold-top"><CardContent className="p-3"><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{l}</p><p className="text-lg font-bold text-maroon-900">{v}</p></CardContent></Card>
        ))}
      </div>

      {isSA && !f.temple_id && byTemple.length > 1 && (
        <Card className="mb-4"><CardContent className="p-3 overflow-x-auto">
          <p className="font-heading font-bold text-maroon-900 mb-2">Consolidated (temple-wise)</p>
          <table className="w-full text-sm" data-testid="hundial-consolidated">
            <thead><tr className="text-left text-[11px] uppercase text-gold-700 border-b"><th className="py-1">Temple</th><th>Reports</th><th className="text-right">Total Rupees</th><th className="text-right">Gold</th><th className="text-right">Silver</th><th className="text-right">Old Notes</th></tr></thead>
            <tbody>{byTemple.map((t) => (
              <tr key={t.temple_id} className="border-b border-[#F5EDE0]"><td className="py-1.5">{tname(t.temple_id)}</td><td>{t.reports}</td><td className="text-right font-semibold">{inr(t.cash)}</td><td className="text-right">{fmtG(t.gold)}</td><td className="text-right">{fmtG(t.silver)}</td><td className="text-right">{inr(t.old)}</td></tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="hundial-table">
          <thead><tr className="bg-maroon-800 text-white text-left text-xs">
            {[...head, "WhatsApp", "Actions"].map((h) => <th key={h} className="px-2 py-2 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={head.length + 2} className="py-8 text-center text-slate-400">No Hundial reports yet</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-[#F0E6D2] align-top">
                {line(r, i).map((v, j) => <td key={j} className={`px-2 py-2 whitespace-nowrap ${head[j] === "Total Rupees" ? "font-bold text-maroon-900" : ""}`}>{v}</td>)}
                <td className="px-2 py-2"><WaStatus items={r.whatsapp} /></td>
                <td className="px-2 py-2 whitespace-nowrap">
                  <Button size="sm" variant="ghost" title="Download PDF" onClick={() => downloadHundialPdf(r)}><FileDown className="w-4 h-4" /></Button>
                  {canSend && <Button size="sm" variant="ghost" title="Send / resend on WhatsApp" onClick={() => resend(r)}><Send className="w-4 h-4 text-green-700" /></Button>}
                  {isSA && <Button size="sm" variant="ghost" title="Delete" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="bg-gold-50 font-bold text-maroon-900 text-sm whitespace-nowrap">
              <td colSpan={isSA ? 5 : 4} className="px-2 py-2">Total ({rows.length} reports)</td><td className="px-2">{totals.days}</td>
              <td className="px-2">{fmtG(totals.gold)}</td><td className="px-2">{fmtG(totals.silver)}</td><td /><td />
              <td className="px-2">{inr(totals.cash)}</td><td /><td className="px-2">{inr(totals.old)}</td><td colSpan={2} />
            </tr></tfoot>
          )}
        </table>
      </CardContent></Card>
    </div>
  );
}
